import MapBoxMap from 'components/mapboxmap/MapBoxMap';
import {
  ItineraryEvent,
  ItineraryStop,
  ItineraryVersion,
} from 'core/graphql/types';
import useAppSelector from 'core/hooks/useAppSelector';
import { selectZoomLevel } from 'pages/itinerary/MapSlice';
import { ReactNode, useMemo, useState } from 'react';
import { Layer, Marker, Popup, Source } from 'react-map-gl';

import MarkerContent from 'components/mapSection/components/MarkerContent';
import PopupContent, {
  PopupContentProps,
} from 'components/mapSection/components/PopupContent';
import { arrowStopsLayer } from 'components/mapSection/components/polylines/ArrowLayer';
import { stopsLineLayer } from 'components/mapSection/components/polylines/LineLayer';
import useItineraryPolyline from '../../core/hooks/useItineraryPolyline';

export interface ItineraryComparisonMapSectionProps {
  itinerary: ItineraryVersion | undefined;
  events: ItineraryEvent[];
  mapId: string;
  hoveredStop: any;
  setHoveredState: (stopId: any) => void;
}

const ItineraryComparisonMapSection = ({
  itinerary,
  events,
  mapId,
  hoveredStop,
  setHoveredState,
}: ItineraryComparisonMapSectionProps) => {
  const mapZoomLevel = useAppSelector(selectZoomLevel);
  const [popupInfo, setPopupInfo] = useState<PopupContentProps | null>(null);
  const { itineraryPolyline } = useItineraryPolyline(itinerary);

  const markers = useMemo(() => {
    const mixedArray: ItineraryStop[] = itinerary?.itineraryStops ?? [];
    return mixedArray
      .filter((stop) => stop.latitude && stop.longitude)
      .map((stop, order) => {
        const stopEvents = events.filter(
          (d) => stop.identifiers?.split(',').includes(d.identifier),
        );
        const shouldHighlight = stop.address === hoveredStop;
        return (
          <div
            key={stop.id}
            onMouseOver={() => setHoveredState(stop.address)}
            onFocus={() => {}}
            onMouseOut={() => setHoveredState(null)}
            onBlur={() => {}}
          >
            <Marker
              key={`marker-${order + 1}`}
              longitude={stop.longitude!}
              latitude={stop.latitude!}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setPopupInfo({
                  itineraryStop: stop,
                  order: order + 1,
                  events: stopEvents,
                });
              }}
            >
              <div className={shouldHighlight ? 'shadow-highlight' : ''}>
                <MarkerContent
                  itineraryStop={stop}
                  order={order + 1}
                  events={stopEvents}
                />
              </div>
            </Marker>
          </div>
        );
      });
  }, [itinerary, events, hoveredStop, setHoveredState]);

  const startStopMakers = useMemo(() => {
    const markerArray: ReactNode[] = [];
    if (
      itinerary?.startLatitude === itinerary?.endLatitude &&
      itinerary?.startLongitude === itinerary?.endLongitude &&
      itinerary?.startLatitude &&
      itinerary?.startLongitude
    ) {
      markerArray.push(
        <Marker
          key="marker-start-stop"
          longitude={itinerary?.startLongitude}
          latitude={itinerary?.startLatitude}
          anchor="center"
        >
          <div className="rounded-lg bg-light-black p-1">Start/Stop</div>
        </Marker>,
      );
    } else {
      if (itinerary?.startLatitude && itinerary?.startLongitude) {
        markerArray.push(
          <Marker
            key="marker-start"
            longitude={itinerary?.startLongitude}
            latitude={itinerary?.startLatitude}
            anchor="center"
          >
            <div className="rounded-lg bg-light-black p-1">Start</div>
          </Marker>,
        );
      }

      if (itinerary?.endLatitude && itinerary?.endLongitude) {
        markerArray.push(
          <Marker
            key="marker-start-stop"
            longitude={itinerary?.endLongitude}
            latitude={itinerary?.endLatitude}
            anchor="center"
          >
            <div className="rounded-lg bg-light-black p-1">Stop</div>
          </Marker>,
        );
      }
    }
    return markerArray;
  }, [
    itinerary?.endLatitude,
    itinerary?.endLongitude,
    itinerary?.startLatitude,
    itinerary?.startLongitude,
  ]);

  return (
    <div className="h-full flex-grow dark:bg-black dark:text-white">
      <MapBoxMap id={mapId} zoomLevel={mapZoomLevel} interactiveLayerIds={undefined}>
        {startStopMakers}
        {markers}
        {popupInfo && (
          <Popup
            anchor="bottom"
            latitude={Number(popupInfo.itineraryStop.latitude)}
            longitude={Number(popupInfo.itineraryStop.longitude)}
            onClose={() => setPopupInfo(null)}
            className="text-black"
            offset={30}
          >
            <PopupContent
              itineraryStop={popupInfo.itineraryStop}
              order={popupInfo.order}
              events={popupInfo.events}
            />
          </Popup>
        )}
        <Source type="geojson" data={itineraryPolyline}>
          <Layer {...stopsLineLayer} />
          <Layer {...arrowStopsLayer} />
        </Source>
      </MapBoxMap>
    </div>
  );
};

export default ItineraryComparisonMapSection;
